import React from 'react';
import { Button, Modal, ProfilePicture } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import { marked } from 'marked';
import dompurify from 'dompurify';
import { Eye, EyeOff } from 'lucide-react';
import PropTypes from 'prop-types';

import { NoteForm } from '../noteForm';
import { NoteMenu } from './menu';
import { useNote } from './useNote';

export const Note = ({ queryParams, note, type, contactId }) => {
  const {
    isEditing,
    setIsEditing,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    isLoadingUpdateNote,
    isLoadingDeleteNote,
    onEdit,
    onSave,
    onShowDeleteConfirmation,
    onDelete,
  } = useNote({ queryParams, contactId, note });

  return isEditing ? (
    <NoteForm note={note} onSuccess={onSave} onClose={() => setIsEditing(false)} isLoading={isLoadingUpdateNote} />
  ) : (
    <Flex width="100%" key={note.id} flexDirection="column" sx={{ gap: '8px' }}>
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" sx={{ gap: '8px' }}>
          <Box width="24px" height="24px" flex="0 0 auto">
            <ProfilePicture name={note.author.fullName} imageUrl={note.author.avatarUrl} />
          </Box>
          <Text fontSize="14px" fontWeight="medium">
            {note.author.fullName}
          </Text>
          <Text fontSize="14px" color="text.subtle">
            {new Date(note?.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </Text>
          {type !== 'public' && (
            <>
              {note.external ? (
                <Flex alignItems="center" color="text.subtle" sx={{ gap: '8px' }}>
                  <Box as={Eye} width="16px" height="16px" aria-hidden="true" />
                  <Text fontSize="14px" fontWeight="medium">
                    Visible to companies
                  </Text>
                </Flex>
              ) : (
                <Flex alignItems="center" color="text.subtle" sx={{ gap: '8px' }}>
                  <Box as={EyeOff} width="16px" height="16px" aria-hidden="true" />
                  <Text fontSize="14px" fontWeight="medium">
                    Internal
                  </Text>
                </Flex>
              )}
            </>
          )}
        </Flex>
        {type !== 'public' && <NoteMenu onEdit={onEdit} onDelete={onShowDeleteConfirmation} />}
      </Flex>
      <Text fontSize="14px" fontWeight="medium" color="text.dark">
        {note.title}
      </Text>
      <Text fontSize="14px">
        <Box
          dangerouslySetInnerHTML={{
            __html: dompurify.sanitize(marked(note?.body || '')),
          }}
          sx={{
            '&& *:first-child': {
              mt: 0,
              marginBlockStart: '0 !important',
            },
            '&& h1, h2': {
              color: 'text',
            },
            '&& strong': {
              fontWeight: 'medium',
            },
            '&& p': {
              mb: '0',
            },
            '&& p + p': {
              mt: '8px',
            },
          }}
        />
      </Text>
      {showDeleteConfirmation && (
        <Modal
          title="Delete note?"
          actions={
            <Flex justifyContent="flex-end" sx={{ columnGap: '8px' }}>
              <Button variant="secondary" onClick={() => setShowDeleteConfirmation(false)}>
                Cancel
              </Button>
              <Button variant="danger" loading={isLoadingDeleteNote} onClick={onDelete}>
                Delete note
              </Button>
            </Flex>
          }
          onCancel={() => setShowDeleteConfirmation(false)}
        >
          <Text fontSize="14px">The note will be deleted for every user.</Text>
        </Modal>
      )}
    </Flex>
  );
};

Note.propTypes = {
  queryParams: PropTypes.object,
  note: PropTypes.object,
  type: PropTypes.oneOf(['default', 'read', 'matches', 'public']),
  contactId: PropTypes.number.isRequired,
};

Note.defaultProps = {
  queryParams: {},
  note: {},
  type: 'default',
};
